// PILNE ticker (views/home/header.php): a continuous right-to-left loop.
// The list is repeated (clones hidden from assistive tech and the tab
// order) so the loop has no gap; the first frame keeps the comp's centred
// layout. Pauses on hover and keyboard focus. With prefers-reduced-motion it
// stands still (no clones, no loop), but a focused link is still shifted
// into view — a static offset is not motion, and the centred list overflows
// the track on both sides.

const SPEED = 50; // px per second

export function initTicker() {
  const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  document.querySelectorAll('.hp-ticker__track').forEach((track) => {
    const list = track.querySelector('.hp-ticker__list');
    const items = list ? [...list.children] : [];
    if (!items.length || track.dataset.marquee) return;
    track.dataset.marquee = '1';

    const setup = () => {
      // Measure the designed (centred) layout first.
      list.querySelectorAll('[data-ticker-clone]').forEach((clone) => clone.remove());
      track.classList.remove('is-marquee');
      list.style.removeProperty('--ticker-start');

      if (still) return;

      const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
      const trackBox = track.getBoundingClientRect();
      const start = items[0].getBoundingClientRect().left - trackBox.left;
      const setWidth = items.reduce((sum, item) => sum + item.getBoundingClientRect().width + gap, 0);
      if (!setWidth || !trackBox.width) return;

      // Enough copies to cover the track while the first set scrolls out.
      const copies = Math.max(1, Math.ceil((trackBox.width + Math.abs(start)) / setWidth));
      for (let c = 0; c < copies; c += 1) {
        items.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.dataset.tickerClone = '';
          clone.setAttribute('aria-hidden', 'true');
          clone.querySelectorAll('a').forEach((a) => a.setAttribute('tabindex', '-1'));
          list.append(clone);
        });
      }

      list.style.setProperty('--ticker-start', `${start}px`);
      list.style.setProperty('--ticker-shift', `${-setWidth}px`);
      list.style.setProperty('--ticker-duration', `${(setWidth / SPEED).toFixed(1)}s`);
      track.classList.add('is-marquee');
    };

    setup();

    // The CSS pause (`:focus-within`) stops the strip wherever it stands, so
    // a focused link was measured at x = -462 — off-screen, with its focus
    // ring invisible. While a real link holds focus the animation is dropped
    // for an explicit offset that puts that link back in view; letting go
    // restores the loop. Positions are read as the item's box relative to the
    // list's, which is unaffected by whatever transform is in force.
    const reveal = (event) => {
      const link = event.target.closest('a');
      if (!link || link.closest('[data-ticker-clone]')) {
        return;
      }
      const item = link.closest('li') || link;
      const box = item.getBoundingClientRect();
      const listLeft = list.getBoundingClientRect().left;
      const within = box.left - listLeft;
      let x = Math.min(0, -(within + box.width / 2 - track.clientWidth / 2));

      if (still) {
        // No loop to fall back on: centre the link, but never past the ends
        // of the real items (the list overflows the track on both sides).
        const first = items[0].getBoundingClientRect().left - listLeft;
        const end = items[items.length - 1].getBoundingClientRect().right - listLeft;
        x = end - first <= track.clientWidth
          ? 0
          : Math.max(track.clientWidth - end, Math.min(-first, -(within + box.width / 2 - track.clientWidth / 2)));
      }

      list.style.animation = 'none';
      list.style.transform = `translateX(${x}px)`;
    };

    const release = () => {
      list.style.removeProperty('animation');
      list.style.removeProperty('transform');
    };

    track.addEventListener('focusin', reveal);
    track.addEventListener('focusout', release);

    let width = track.clientWidth;
    const onResize = () => {
      if (track.clientWidth !== width) {
        width = track.clientWidth;
        setup();
      }
    };
    // ResizeObserver arrived in Safari 13.1; older ones fall back to window resizes.
    if ('ResizeObserver' in window) new ResizeObserver(onResize).observe(track);
    else window.addEventListener('resize', onResize, { passive: true });
  });
}
